<template>
  <div class="interview-container">
    <div v-show="showCalendar" class="date-selector">
      <div class="calendar-header">
        <div class="pre-btn" @click="handlePreMonthBtnClick">
          <i class="el-icon-arrow-left"></i>
        </div>
        <div class="date-title">
          {{ dateTitle }}
        </div>
        <div class="next-btn" @click="handleNextMonthBtnClick">
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
      <div class="week">
        <div v-for="(item, index) in week" class="day" :key="index">{{ item }}</div>
      </div>
      <el-calendar v-model="search.selectDate" class="calendar" ref="calendar">
        <template #dateCell="{ data }">
          <div
            class="day"
            :class="[
              selectedDayRange.includes(data.day) && 'select-week',
              selectedDayRange[0] === data.day && 'monday',
              selectedDayRange[6] === data.day && 'sunday',
            ]"
          >
            {{ data.day.split('-').slice(2).join('-') }}
          </div>
        </template>
      </el-calendar>
    </div>
    <div class="interview-table-container">
      <div class="header">
        <div v-show="listType === LIST_TYPE.TABLE" class="title">{{ $t(`${i18nPrefix}.title.interviewManagement`) }}</div>
        <div v-show="listType === LIST_TYPE.CALENDAR" class="calendar-operation">
          <div class="today" @click="handleTodayBtnClick">{{ $t(`${i18nPrefix}.label.today`) }}</div>
          <div class="pre-week" @click="handlePreWeekBtnClick"><i class="el-icon-arrow-left"></i></div>
          <div class="next-week" @click="handleNextWeekBtnClick"><i class="el-icon-arrow-right"></i></div>
          <div class="date-range">{{ dateRange }}</div>
        </div>
        <div class="search" :class="showCalendar && 'show-calendar'">
          <el-input
            v-model="search.keywords"
            clearable
            class="keywords-searcher"
            prefix-icon="el-icon-search"
            :placeholder="$t(`${i18nPrefix}.placeholder.keywordSearch`)"
            @input="handleSearchChange"
          >
          </el-input>
          <el-select
            v-model="search.status"
            clearable
            multiple
            class="status-selector"
            :placeholder="$t(`${i18nPrefix}.placeholder.allStatus`)"
            @change="handleSearchChange"
          >
            <el-option
              v-for="(value, key) in INTERVIEW_STATUS"
              :key="key"
              :value="value"
              :label="$t(`${i18nPrefix}.label.${key}`)"
            >
            </el-option>
          </el-select>
        </div>
        <div class="list-type-button" @click="onListTypeBtnClick">
          <i class="btn-icon iconfont" :class="showTable ? 'iconliebiaomoshibeifen' : 'iconliebiao' "></i>
        </div>
      </div>
      <InterviewTable
        v-show="showTable"
        class="interview-table"
        isScrollable
        showCompany
        showJob
        singleActionBtn
        :loading="loading"
        :data="listData"
        :tableColumnWidthMap="tableColumnWidthMap"
        @search="handleSearch"
        @edit="handleEdit"
        @cancel="handleCancel"
        @resend="handleResend"
        @sendNew="handleSendNew"
        @sendRating="handleSendRating"
        @confirmInterview="handleConfirmInterview"
        @viewGroupInterview="handleViewGroupInterview"
        @scrollBottom="handleScrollBottom"
        @openCandidateDetail="openCandidateDetail"
      />
      <CalendarTable
        v-if="selectedDateRange.length > 0"
        v-show="showCalendar"
        class="calendar-container"
        :showCalendar="showCalendar"
        :loading="loading"
        :selectedDateRange="selectedDateRange"
        :calendarTableLoading="calendarTableLoading"
        @edit="handleEdit"
        @cancel="handleCancel"
        @resend="handleResend"
        @sendNew="handleSendNew"
        @sendRating="handleSendRating"
        @confirmInterview="handleConfirmInterview"
        @openCandidateDetail="openCandidateDetail"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { INTERVIEW_STATUS, LIST_TYPE } from '@/constant/interview'
import InterviewTable from '@/components/interview/interviewTable.vue'
import CalendarTable from './components/calendarTable.vue'

export default {
  components: {
    InterviewTable,
    CalendarTable
  },
  props: {
    listData: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    listType: {
      type: String,
      required: true
    },
    search: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      INTERVIEW_STATUS,
      LIST_TYPE,
      emitSearch: null,
      emitScrollBottom: null,
      atBottom: false,
      tableColumnWidthMap: {
        interviewDate: 205,
        companyName: 150,
        jobName: 150,
        recruiter: 138,
        hiringManagerNames: 145,
        candidateNames: 145,
        status: 125,
        interviewResult: 125,
        action: 120
      },
      selectedDateRange: [],
      calendarTableLoading: false
    }
  },
  computed: {
    hasInterviewPermission () {
      return this.hasAnyPermission(['PREMIUM_INTERVIEW_SCHEDULING', 'PERM_HR_INTERVIEW_SCHEDULING'])
    },
    showTable () {
      return this.listType === LIST_TYPE.TABLE
    },
    showCalendar () {
      return this.listType === LIST_TYPE.CALENDAR
    },
    dateTitle () {
      const locale = this.$i18n.locale
      if (locale === 'en') {
        moment.locale('en')
        return moment(this.search.selectDate).format('MMM YYYY')
      } else {
        moment.locale('zh-cn')
        return moment(this.search.selectDate).format('YYYY[年]MMM')
      }
    },
    selectedDayRange () {
      return _.map(this.selectedDateRange, item => item.day)
    },
    week () {
      const locale = this.$i18n.locale
      if (locale === 'en') {
        moment.locale('en')
      } else {
        moment.locale('zh-cn')
      }
      return _.map(this.selectedDayRange, day => moment(day).format('dd'))
    },
    dateRange () {
      const first = this.selectedDayRange[0]
      const last = _.last(this.selectedDayRange)

      const locale = this.$i18n.locale
      if (locale === 'en') {
        moment.locale('en')
        return `${moment(first).format('MMM Do, YYYY')} - ${moment(last).format('MMM Do, YYYY')}`
      } else {
        moment.locale('zh-cn')
        return `${moment(first).format('YYYY[年]MMMDo')} - ${moment(last).format('YYYY[年]MMMDo')}`
      }
    }
  },
  created () {

  },
  mounted () {
    this.emitSearch = _.debounce(() => {
      if (this.search.dayRange !== this.selectedDayRange) {
        this.search.dayRange = this.selectedDayRange
      }
      this.$emit('search')
    }, 500)
  },
  methods: {
    handleCommand (record, command) {
      if (command === 'sendInterviewRating') {
        this.handleSendRating(record)
      }
    },
    handleEdit (record) {
      this.$emit('edit', record)
    },
    handleCancel (record) {
      this.$emit('cancel', record)
    },
    handleResend (record) {
      this.$emit('resend', record)
    },
    handleSendNew (record) {
      this.$emit('sendNew', record)
    },
    handleSendRating (record) {
      this.$emit('sendRating', record)
    },
    handleConfirmInterview (record) {
      this.$emit('confirmInterview', record)
    },
    handleViewGroupInterview (record) {
      this.$emit('viewGroupInterview', record)
    },
    handleSearch (keywords) {
      this.$emit('search', keywords)
    },
    onListTypeBtnClick () {
      this.search.dayRange = this.selectedDayRange
      this.$emit('listTypeChange')
    },
    handleScrollBottom () {
      this.$emit('scrollBottom', this.search)
    },
    handlePreMonthBtnClick () {
      this.$refs.calendar.selectDate('prev-month')
    },
    handleNextMonthBtnClick () {
      this.$refs.calendar.selectDate('next-month')
    },
    handleTodayBtnClick () {
      this.$refs.calendar.selectDate('today')
    },
    handlePreWeekBtnClick () {
      this.search.selectDate = moment(this.search.selectDate).subtract(1, 'week').format('YYYY-MM-DD')
    },
    handleNextWeekBtnClick () {
      this.search.selectDate = moment(this.search.selectDate).add(1, 'week').format('YYYY-MM-DD')
    },
    formatDateRange () {
      const date = moment(this.search.selectDate).format('YYYY-MM-DD')
      const weekOfDay = moment(date, 'YYYY-MM-DD').format('E')
      const lastMonday = moment(date).subtract(weekOfDay - 1, 'days').format('YYYY-MM-DD')
      const list = []
      const vm = this
      for (let i = 0; i < 7; i++) {
        const isToday = moment(lastMonday).add(i, 'day').isSame(moment(), 'day')
        const day = {
          day: moment(lastMonday).add(i, 'day').format('YYYY-MM-DD'),
          dayOfMonth: moment(lastMonday).add(i, 'day').format('DD'),
          records: [],
          isToday
        }
        Object.defineProperty(day, 'dayOfWeek', {
          configurable: true,
          enumerable: true,
          get () {
            const locale = vm.$i18n.locale
            if (locale === 'en') {
              moment.locale('en')
            } else {
              moment.locale('zh-cn')
            }
            return moment(lastMonday).add(i, 'day').format('dddd')
          }
        })

        list.push(day)
      }
      this.selectedDateRange = list
    },
    formatData () {
      this.calendarTableLoading = true
      _.forEach(this.selectedDateRange, item => {
        item.records = []
      })
      _.forEach(this.listData, interview => {
        if (!interview.interview_time) {
          return
        }
        const interviewDay = moment(interview.interview_time).format('YYYY-MM-DD')
        const interviewStartTime = moment(interview.interview_time).format('HH:mm')
        const interviewEndTime = moment(interview.interview_end_time).format('HH:mm')
        interview._vid = interview.uuid
        interview.sortTime = interview.interview_time
        interview.startTime = interviewStartTime
        interview.endTime = interviewEndTime
        if (interview.interview_time) {
          const startTime = interviewStartTime.split(':')
          const start = parseInt(startTime[0]) + parseInt(startTime[1]) / 60
          interview.start = start
        }
        if (interview.interview_end_time) {
          const endTime = interviewEndTime.split(':')
          const end = parseInt(endTime[0]) + parseInt(endTime[1]) / 60
          if (end - interview.start <= 0.5) {
            interview.end = interview.start + 0.5
          } else if (end - interview.start <= 1) {
            interview.end = interview.start + 1
          } else if (end - interview.start <= 1.5) {
            interview.end = interview.start + 1.5
          } else if (end - interview.start <= 2) {
            interview.end = interview.start + 2
          } else {
            interview.end = end
          }
          interview.durationEnd = end
        }

        const targetDay = _.find(this.selectedDateRange, day => moment(day.day, 'YYYY-MM-DD').isSame(interviewDay, 'day'))
        if (targetDay) {
          targetDay.records.push(interview)
        }
      })

      // format 尚未确定面试时间的面试
      _.forEach(this.listData, interview => {
        if (interview.interview_time) {
          return
        }
        _.forEach(interview.available_interview_time_list, (interviewTime, index) => {
          const availableInterview = { ...interview }
          const interviewDay = moment(interviewTime.startDate).format('YYYY-MM-DD')
          const interviewStartTime = moment(interviewTime.startTime, 'HH:mm:ss').format('HH:mm')
          const interviewEndTime = moment(interviewTime.endTime, 'HH:mm:ss').format('HH:mm')
          availableInterview._vid = `${interview.uuid}-${index}`
          availableInterview.sortTime = `${interviewDay} ${interviewStartTime}`
          availableInterview.startTime = interviewStartTime
          availableInterview.endTime = interviewEndTime
          const startTime = interviewStartTime.split(':')
          availableInterview.start = parseInt(startTime[0]) + parseInt(startTime[1]) / 60
          const endTime = interviewEndTime.split(':')
          const end = parseInt(endTime[0]) + parseInt(endTime[1]) / 60
          if (end - availableInterview.start <= 0.5) {
            availableInterview.end = availableInterview.start + 0.5
          } else if (end - availableInterview.start <= 1) {
            availableInterview.end = availableInterview.start + 1
          } else if (end - availableInterview.start <= 1.5) {
            availableInterview.end = availableInterview.start + 1.5
          } else if (end - availableInterview.start <= 2) {
            availableInterview.end = availableInterview.start + 2
          } else {
            availableInterview.end = end
          }
          availableInterview.durationEnd = end

          const targetDay = _.find(this.selectedDateRange, day => moment(day.day, 'YYYY-MM-DD').isSame(interviewDay, 'day'))
          if (targetDay) {
            targetDay.records.push(availableInterview)
          }
        })
      })

      _.forEach(this.selectedDateRange, day => {
        if (day.records.length < 1) {
          return
        }
        const records = _.sortBy(day.records, (record) => moment(record.sortTime))
        const list = []
        let endTime = null
        let childEndTime = null
        let endChildId = null
        _.forEach(records, record => {
          const hasChildren = _.chain(records)
            .filter(item => item.uuid !== record.uuid)
            .some(item => record.start >= item.start && record.start < item.end)
            .value()

          if (!endTime) {
            list.push({
              ...record,
              hasChildren,
              childrenExpend: false,
              children: []
            })
            endTime = record.end
          } else if (record.start >= endTime) {
            list.push({
              ...record,
              hasChildren,
              childrenExpend: false,
              children: []
            })
            endTime = record.end
          } else {
            if (childEndTime === null) {
              list[list.length - 1].children.push(record)
              childEndTime = record.end
              endChildId = record.uuid
            } else if (record.start < childEndTime) {
              const parent = _.find(list, item => _.some(item.children, { uuid: endChildId }))
              childEndTime = record.end > childEndTime ? record.end : childEndTime
              parent.children.push(record)
            } else {
              list[list.length - 1].children.push(record)
              childEndTime = record.end > childEndTime ? record.end : childEndTime
              endChildId = record.uuid
            }
          }
        })

        day.records = list
      })
      this.calendarTableLoading = false
    },
    handleSearchChange () {
      this.emitSearch && this.emitSearch()
    },
    openCandidateDetail (rowData) {
      const candidateId = rowData.candidate[0].id
      const jobId = rowData.job_id
      window.open(`/v-admin/application/profile/interview?candidate_id=${candidateId}&job_id=${jobId}&hidden_bread_crumb=1`)
    }
  },
  watch: {
    'search.selectDate': {
      immediate: true,
      deep: true,
      handler () {
        this.formatDateRange()
        this.handleSearchChange()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.interview-container {
  overflow: hidden;
  display: flex;
  margin-top: 10px;

  .date-selector {
    padding: 20px 24px;
    background-color: #fff;
    box-shadow: 0px 3px 6px -4px rgba(0,0,0, 0.12);
    margin-right: 14px;

    .calendar-header {
      display: flex;
      align-items: center;
      padding: 0 3px;
      margin-bottom: 18px;

      .date-title {
        margin: 0 auto;
        color: #606266;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;
      }

      .pre-btn,
      .next-btn {
        cursor: pointer;
        color: #3d3d3d;
      }
    }

    .week {
      display: flex;
      justify-content: space-around;
      margin-bottom: 16px;

      .day {
        width: 20px;
        height: 20px;
        line-height: 20px;
        color: #909399;
        text-align: center;
        font-weight: 500;
      }
    }

    .calendar {
      ::v-deep {
        .el-calendar__header {
          display: none;
        }

        .el-calendar__body {
          padding: 0;

          thead {
            display: none;
          }

          .el-calendar-table {
            width: auto;

            .el-calendar-table__row {
              .el-calendar-day {
                height: 30px;
                width: 30px;
                color: #606266;
                border-radius: 50%;
                text-align: center;
                position: relative;
                padding: 0;

                .day {
                  position: absolute;
                  inset: 0;
                  height: 30px;
                  line-height: 30px;

                  &.select-week {
                    background-color: #F2FCFF;

                    &.monday {
                      border-radius: 50% 0 0 50%;
                    }

                    &.sunday {
                      border-radius: 0 50% 50% 0;
                    }
                  }
                }

                &:hover {
                  background-color: #0096cc;
                  color: #fff;

                  .select-week {
                    color: #606266;
                  }
                }
              }

              .current {
                border-radius: 50%;

                &.is-selected {
                  background: #0096cc;

                  .el-calendar-day {
                    color: #fff;

                    .day {
                      position: relative;
                      background-color: initial;
                    }

                    &:hover {
                      .select-week {
                        color: #fff;
                      }
                    }
                  }
                }
              }

              .next, .prev {
                .el-calendar-day {
                  color: #DCDFE6;
                }
              }
            }

            td {
              border: 0;
            }
          }
        }
      }
    }
  }

  .interview-table-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    padding: 20px 24px;
    overflow: hidden;
    box-shadow: 0px 3px 6px -4px rgba(0,0,0, 0.12);

    .header {
      display: flex;
      align-items: center;
      padding-right: 18px;

      .title {
        color: #303133;
        font-size: 18px;
        font-weight: 500;
        line-height: 25px;
      }

      .calendar-operation {
        display: flex;
        align-items: center;
        margin-left: 20px;
        margin-right: 67px;
        color: #606266;

        .today {
          font-size: 12px;
          height: 25px;
          line-height: 25px;
          padding: 0 10px;
          background-color: #F2FCFF;
          border-radius: 4px;
          text-align: center;
          cursor: pointer;
        }

        .pre-week,
        .next-week {
          margin-left: 14px;
          color: #3d3d3d;
          font-size: 12px;
          cursor: pointer;
        }

        .date-range {
          margin-left: 14px;
          width: fit-content;
          font-size: 14px;
          line-height: 20px;
          font-weight: 500;
          white-space: nowrap;
        }
      }

      .search {
        display: flex;
        align-items: center;
        margin-left: auto;

        .keywords-searcher {
          width: 400px;
        }

        .status-selector {
          width: 470px;
          margin-left: 16px;
        }

        &.show-calendar {
          flex: 1;
          max-width: 870px;

          .keywords-searcher {
            width: 35%;
          }

          .status-selector {
            width: 65%;
          }
        }

        ::v-deep .el-input__inner {
          border-radius: 0;
          height: 32px;
          line-height: 32px;

          .el-input__prefix,
          .el-input__suffix {
            .el-input__icon {
              line-height: inherit;
            }
          }

          &:focus {
            border-color: #b2b2b2;
          }

          &:hover {
            border-color: #C0C4CC;
          }
        }
      }

      .list-type-button {
        margin-left: 24px;

        .btn-icon {
          font-size: 26px;
          cursor: pointer;
        }
      }
    }

    .interview-table {
      flex: 1;
      border: 0;
      overflow: hidden;
      margin-top: 20px;

      ::v-deep .el-table__header {
        tr {
          th:first-child {
            padding-left: 30px;
          }
        }
      }

      ::v-deep .el-table__body {
        .el-table__row {
          .el-table__cell:first-child {
            padding-left: 30px;
          }

          .interview-date {
            .el-table__expand-icon {
              left: 10px;
            }
          }
        }
      }

      ::v-deep .el-table__body-wrapper{
        overflow: auto;
      }
    }

    .calendar-container {
      margin-top: 14px;
    }
  }
}
</style>
